import React, { useEffect, useState } from "react";
import clsx from "clsx";
import {
  Button,
  ButtonGroup,
  Card,
  OverlayTrigger,
  Spinner,
  Stack,
  Tooltip,
} from "react-bootstrap";
import styles from "./style/CourseCard.module.css";
import { number, object, oneOf, oneOfType, shape, string } from "prop-types";
import moment from "moment";
import { DEFAULT_IMAGE_SRC, INSTRUCTOR_ROLE, STUDENT_ROLE } from "@constants";
import { Link, useNavigate } from "react-router-dom";
import { AccessControl } from "@components";
import { getDiscountValid, replaceErrorImage } from "@helpers";
import { useAccessControl } from "@hooks";
import { useDispatch, useSelector } from "react-redux";
import { addCourseToCartAction, enrollAction } from "@redux/actions";
import { formatTimeHMS } from "@helpers";
import {
  deleteSavedCourseAction,
  saveCourseAction,
} from "@redux/modules/course/slices";
import { LearnType } from "./LearnType";
import { ManageType } from "./ManageType";

const CourseCard = ({ variant, data, type }) => {
  const { enroll } = useSelector((state) => state.enrollment);
  const { processing: enrollProcessing, courseId: courseIdEnroll } = enroll;
  const profile = useSelector((state) => state.user.profile.data);

  const { saveCourse, deleteSavedCourse } = useSelector(
    (state) => state.course
  );
  const {
    data: courseIdSave,
    isSuccess: saveSuccess,
    processing: saveProcessing,
  } = saveCourse;
  const {
    data: courseIdRemoveSave,
    isSuccess: removeSavedSuccess,
    processing: removeSavedProcessing,
  } = deleteSavedCourse;

  const saveCourseProcessing = saveProcessing || removeSavedProcessing;

  const [isSavedState, setIsSavedState] = useState(false);

  // hooks
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuth } = useAccessControl();

  const handleEnroll = () => {
    if (!isAuth) {
      navigate("/sign-in");
    } else {
      dispatch(
        enrollAction({ courseId: id, callback: handleNavigateLearning })
      );
    }
  };

  const handleClickSave = () => {
    if (saveCourseProcessing) return;
    if (!id) return;

    if (isSavedState) {
      setIsSavedState(false);
      dispatch(deleteSavedCourseAction({ courseId: id }));
    } else {
      setIsSavedState(true);
      dispatch(saveCourseAction({ courseId: id }));
    }
  };

  const viewDetails = () => {
    if (id) navigate(`/course/${id}`);
    else navigate("/404");
  };

  // variables
  const {
    id,
    title,
    category,
    createdAt,
    price,
    thumbnailUrl,
    user,
    isSave = false,
    percentCompletion,
    avgRate,
    totalTime,
    isEnroll,
    isPurchased,
    isFree,
    isDiscount,
    myRating,
    discounts,
  } = data;

  let imageUrl = DEFAULT_IMAGE_SRC;
  let cardVariant = null;
  const wasCreatedSince = moment(createdAt).add(7, "hours").fromNow();

  // UI partials
  const saveCourseButton = (
    <AccessControl targetRoles={[STUDENT_ROLE, INSTRUCTOR_ROLE]}>
      <button
        size="sm"
        variant={!isSavedState ? "outline-primary" : "primary"}
        onClick={handleClickSave}
        className={styles.btnSave}
      >
        {isSavedState ? (
          <i className="bi bi-heart-fill" />
        ) : (
          <i className="bi bi-heart" />
        )}
      </button>
    </AccessControl>
  );

  const courseFunctions = (
    <ButtonGroup className={clsx("w-100", styles.cartContainer)}>
      <Button
        size="sm"
        onClick={async () => {
          if (!isAuth) {
            navigate("/sign-in");
            return;
          }
          await dispatch(
            addCourseToCartAction({
              courseId: id,
              navigate: () => {
                navigate("/shopping-cart", {
                  state: { courseId: id },
                });
              },
            })
          );
        }}
        className={clsx(styles.cartContent)}
      >
        Buy now
      </Button>
      <OverlayTrigger
        placement={"top"}
        overlay={<Tooltip>Add to cart</Tooltip>}
      >
        <Button
          size="sm"
          variant="outline-primary"
          onClick={() => {
            if (!isAuth) {
              navigate("/sign-in");
              return;
            }
            dispatch(
              addCourseToCartAction({
                courseId: id,
              })
            );
          }}
          className={clsx(styles.cartContent)}
        >
          <span className="mr-2">
            <i className="bi bi-cart-plus"></i>
          </span>
          <b>
            {getDiscountValid(discounts) !== 0 ? (
              <div className={clsx(styles.discountAppear)}>
                <span>
                  {`$${(price * (1 - getDiscountValid(discounts))).toFixed(2)}`}
                </span>
                <span className={clsx(styles.oldPrice)}>{price}</span>
              </div>
            ) : (
              <span> {`$${price}`}</span>
            )}
          </b>
        </Button>
      </OverlayTrigger>
    </ButtonGroup>
  );

  // const courseFunctions = (
  //   <div className={clsx(styles.action)}>
  //     <i className="bi bi-three-dots-vertical"></i>
  //     <ListGroup className={clsx(styles.actionBox)}>
  //       <ListGroup.Item action>
  //         <div>
  //           <i className="bi bi-share mr-2"></i> Share
  //         </div>
  //       </ListGroup.Item>
  //       <ListGroup.Item action>
  //         <i className="bi bi-heart mr-2"></i>Save
  //       </ListGroup.Item>
  //       <ListGroup.Item action>
  //         <i className="bi bi-slash-circle mr-2"></i>Not Interested
  //       </ListGroup.Item>
  //       <ListGroup.Item action>
  //         <i className="bi bi-flag mr-2"></i>Report
  //       </ListGroup.Item>
  //     </ListGroup>
  //   </div>
  // );

  const courseExtra = (
    <>
      <div className={clsx(styles.duration, "position-absolute p-1 rounded")}>
        {`${formatTimeHMS(totalTime)}`}
      </div>
      {(isFree || isDiscount) && (
        <div
          className={clsx(
            styles.label,
            "position-absolute px-2 py-1 rounded shadow"
          )}
        >
          {isFree && "Free Course"}
          {isDiscount && "On Sale"}
        </div>
      )}
      {avgRate !== 0 && (
        <div
          className={clsx(
            styles.rating,
            "position-absolute px-2 py-1 rounded shadow"
          )}
        >
          <i className="bi bi-star"></i> {avgRate?.toFixed(1)}
        </div>
      )}
    </>
  );

  const buyType = (
    <div>
      <Stack gap={2}>
        <Stack direction="horizontal" className={styles.fs12}>
          <Stack>
            <Link to={`/instructor-profile-view/${user?.id}`}>
              {user?.fullName || "Unknown author"}
            </Link>
            <span>{wasCreatedSince}</span>
          </Stack>
          {saveCourseButton}
        </Stack>
        {isFree ? (
          <Button
            size="sm"
            onClick={handleEnroll}
            className="w-100"
            disabled={enrollProcessing && courseIdEnroll === id}
          >
            {enrollProcessing && courseIdEnroll === id && (
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="mr-2"
              />
            )}
            Enroll
          </Button>
        ) : (
          courseFunctions
        )}
      </Stack>
    </div>
  );

  // logics
  const handleNavigateLearning = () => {
    navigate(`/learning/${id}`);
  };
  if (variant === "horizontal") cardVariant = styles.courseCardHorizontal;

  let actionPart;
  switch (type) {
    case "learn":
      actionPart = (
        <LearnType
          courseId={id}
          isAuth={isAuth}
          percentCompletion={percentCompletion}
          user={user}
          myRating={myRating}
          isEnroll={isEnroll}
          isSaved={isSavedState}
          onSave={handleClickSave}
        />
      );
      break;
    case "manage":
      actionPart = <ManageType data={data} />;
      break;
    default: {
      if (isPurchased || isEnroll) {
        actionPart = (
          <LearnType
            courseId={id}
            isAuth={isAuth}
            percentCompletion={percentCompletion}
            user={user}
            myRating={myRating}
            isEnroll={isEnroll}
            isSaved={isSavedState}
            onSave={handleClickSave}
          />
        );
      } else if (profile.id == user.id) {
        actionPart = <ManageType data={data} />;
      } else {
        actionPart = buyType;
      }
      break;
    }
  }
  /* TODO: update logic for getting imageUrl */
  if ((thumbnailUrl ?? "string") !== "string") imageUrl = thumbnailUrl;

  // const viewDetails = () => {
  //   if (id) navigate(`/course/${id}`);
  //   else navigate("/404");
  // };

  // effects

  useEffect(() => {
    setIsSavedState(isSave);
  }, [isSave]);

  useEffect(() => {
    if (courseIdSave === id && saveSuccess === false) {
      setIsSavedState(false);
    }
  }, [courseIdSave, saveSuccess]);

  useEffect(() => {
    if (courseIdRemoveSave === id && removeSavedSuccess === false) {
      setIsSavedState(true);
    }
  }, [courseIdRemoveSave, removeSavedSuccess]);

  //render
  return (
    <Card className={clsx(cardVariant, styles.courseCard, "p-2")}>
      <div
        className={clsx(styles.cardImg, "position-relative")}
        onClick={viewDetails}
      >
        <div className={styles.cardImgWrapper}>
          <Card.Img variant="top" src={imageUrl} onError={replaceErrorImage} />
          <div className={styles.cardOverlay}></div>
          {courseExtra}
        </div>
        <button className={clsx(styles.playIcon, "position-absolute")}>
          <i className="bi bi-play"></i>
        </button>
      </div>
      <Card.Body className={styles.cardBody}>
        <div className="content-part">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className={clsx(styles.fs12)}>
              {category?.name || "Unknown category"}
            </span>
          </div>
          <Card.Title
            className={clsx(styles.courseTitle, "h6")}
            onClick={viewDetails}
          >
            {title}
          </Card.Title>
        </div>
        <div className="action-part">{actionPart}</div>
      </Card.Body>
    </Card>
  );
};

/* required: display name */
CourseCard.displayName = "CourseCard";

/* required: props types */
CourseCard.propTypes = {
  variant: oneOf(["vertical", "horizontal"]),
  data: shape({
    id: string,
    title: string,
    category: shape({ id: string, name: string }),
    createdAt: oneOfType([string, object]),
    price: number,
    discountPrice: number,
    thumbnailUrl: string,
    user: shape({
      fullName: string,
      email: string,
    }),
  }),
  type: oneOf(["learn", "buy", "manage"]),
};

/* required: default props */
CourseCard.defaultProps = {
  variant: "vertical",
  data: {
    title: "Unknown title",
    category: { name: "Unknown category" },
    createdAt: moment(),
    price: 0,
    thumbnailUrl: DEFAULT_IMAGE_SRC,
    user: { fullName: "Unknown user" },
  },
  type: "buy",
};

export { CourseCard };
